package com.tovtalk.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Error
import androidx.compose.material.icons.filled.Inbox
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.SignalWifiOff
import androidx.compose.material.icons.filled.VideoLibrary
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

@Composable
fun TovTalkEmptyState(
    message: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(TovTalkTheme.spacing.xl),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            text = message,
            style = TovTalkTheme.typography.bodyLarge,
            color = TovTalkTheme.colors.textSecondary,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun TovTalkEmptyState(
    icon: ImageVector,
    title: String,
    modifier: Modifier = Modifier,
    description: String? = null,
    actionText: String? = null,
    onAction: (() -> Unit)? = null,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(TovTalkTheme.spacing.xl),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(TovTalkTheme.sizes.emptyStateIconSize),
            tint = TovTalkTheme.colors.textTertiary
        )
        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))
        Text(
            text = title,
            style = TovTalkTheme.typography.titleMedium,
            color = TovTalkTheme.colors.textPrimary,
            textAlign = TextAlign.Center
        )
        if (!description.isNullOrBlank()) {
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
            Text(
                text = description,
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textSecondary,
                textAlign = TextAlign.Center
            )
        }
        if (actionText != null && onAction != null) {
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))
            TovTalkButton(
                text = actionText,
                onClick = onAction,
                variant = TovTalkButtonVariant.Primary,
                size = TovTalkButtonSize.Medium
            )
        }
    }
}

@Composable
fun TovTalkErrorState(
    message: String,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
) {
    TovTalkEmptyState(
        icon = Icons.Default.Error,
        title = StringProvider.get("empty_error_title"),
        description = message,
        actionText = if (onRetry != null) StringProvider.get("common_try_again") else null,
        onAction = onRetry,
        modifier = modifier
    )
}

@Composable
fun TovTalkNoConnectionState(
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
) {
    TovTalkEmptyState(
        icon = Icons.Default.SignalWifiOff,
        title = StringProvider.get("empty_no_connection_title"),
        description = StringProvider.get("empty_no_connection_desc"),
        actionText = if (onRetry != null) StringProvider.get("common_retry") else null,
        onAction = onRetry,
        modifier = modifier
    )
}

@Composable
fun TovTalkNoResultsState(
    query: String,
    modifier: Modifier = Modifier,
) {
    TovTalkEmptyState(
        icon = Icons.Default.Search,
        title = StringProvider.get("empty_no_results_title"),
        description = StringProvider.get("empty_no_results_desc", query),
        modifier = modifier
    )
}

@Composable
fun TovTalkEmptyFeedState(
    modifier: Modifier = Modifier,
    onExplore: (() -> Unit)? = null,
) {
    TovTalkEmptyState(
        icon = Icons.Default.VideoLibrary,
        title = StringProvider.get("empty_no_videos_title"),
        description = StringProvider.get("empty_no_videos_desc"),
        actionText = if (onExplore != null) StringProvider.get("common_explore") else null,
        onAction = onExplore,
        modifier = modifier
    )
}

@Composable
fun TovTalkResourceNotFoundState(
    modifier: Modifier = Modifier,
    onNavigateToHome: () -> Unit,
) {
    TovTalkEmptyState(
        icon = Icons.Default.Error,
        title = StringProvider.get("error_resource_not_found"),
        description = null,
        actionText = StringProvider.get("common_go_home"),
        onAction = onNavigateToHome,
        modifier = modifier
    )
}

@Composable
fun TovTalkNoPostsState(
    isOwnProfile: Boolean,
    modifier: Modifier = Modifier,
    onUpload: (() -> Unit)? = null,
) {
    TovTalkEmptyState(
        icon = Icons.Default.VideoLibrary,
        title = if (isOwnProfile) StringProvider.get("empty_share_first_video") else StringProvider.get("empty_no_videos_title"),
        description = if (isOwnProfile) StringProvider.get("empty_tap_to_create") else null,
        actionText = if (isOwnProfile && onUpload != null) StringProvider.get("common_create") else null,
        onAction = onUpload,
        modifier = modifier
    )
}

@Composable
fun TovTalkEmptyInboxState(
    modifier: Modifier = Modifier,
) {
    TovTalkEmptyState(
        icon = Icons.Default.Inbox,
        title = StringProvider.get("empty_no_messages_title"),
        description = StringProvider.get("empty_no_messages_desc"),
        modifier = modifier
    )
}
